import Phaser from 'phaser';
import { TELEPORT } from '@/config/balance.config';
import { useGameStore } from '@/ui/store/gameStore';
import type { TeleportData, TeleportTarget } from '@/game/fx/teleport';
import {
  assetBundleManager,
  resolvePlayableBiomeId,
} from '@/game/assets/AssetBundleManager';
import { saveManager } from '@/save';
import { BIOMES_BY_ID } from '@/config/biomes.config';

/**
 * TeleportScene — widowiskowa scena-overlay portalu między scenami.
 *
 * Launchowana przez `startTeleport()` przy zmianie biomu, wejściu na plac i
 * powrocie z placu. Biegnie PONAD bieżącą sceną (jak `HatchScene`) i przeżywa
 * podmianę scen świata.
 *
 * **Timeline** (czasy z `TELEPORT` w balance.config — wszystko proceduralne):
 *   1. COVER  — iskry zlatują się do środka, rdzeń + wir narastają i obracają,
 *               ciemna kurtyna zakrywa ekran. Ładowanie rusza równolegle.
 *   2. WAIT  — jeśli potrzeba, portal pokazuje cel i postęp; bez białej zasłony.
 *   3. (peak) — krótki biały rozbłysk dopiero po przygotowaniu paczki celu.
 *   4. HOLD   — `performSwap()` podmienia świat; nowa scena zwalnia starą paczkę
 *               na początku `create()`, już po shutdownie poprzedniej.
 *   5. REVEAL — rozbłysk gaśnie, portal pęcznieje i znika, iskry wystrzelają,
 *               kurtyna się rozpływa odsłaniając nowy świat.
 *   6. stop() — `SHUTDOWN` zeruje `isTeleporting` (+ bezpiecznik wall-clock).
 *
 * **Reset stanu w `init()`:** Phaser reużywa tę samą instancję sceny przy
 * kolejnych launchach (inicjalizatory pól klasy odpalają się tylko raz przy
 * `new`). Cały zmienny stan ustawiamy w `init()`, który biegnie za każdym
 * launchem — inaczej 2-gi teleport dziedziczyłby stan z 1-go.
 */
export class TeleportScene extends Phaser.Scene {
  private target: TeleportTarget = 'WorldScene';
  private accent = 0x6fd6ff;
  private swapped = false;
  private runId = 0;

  constructor() {
    super({ key: 'TeleportScene' });
  }

  init(data: TeleportData): void {
    // ⚠ Reset PRZY KAŻDYM launchu (patrz docstring klasy).
    this.target = data?.target ?? 'WorldScene';
    this.accent = data?.accent ?? 0x6fd6ff;
    this.swapped = false;
    this.runId++;
  }

  create(): void {
    const runId = this.runId;
    // Flaga ustawiona też w startTeleport() (sync); tu idempotentnie, a SHUTDOWN
    // ZAWSZE ją zeruje (główna ścieżka; startTeleport ma dodatkowy bezpiecznik).
    useGameStore.getState().setTeleporting(true);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      useGameStore.getState().setTeleporting(false);
    });

    const w = this.scale.width;
    const h = this.scale.height;
    const cx = w / 2;
    const cy = h / 2;
    const maxSide = Math.max(w, h);

    const accent = this.accent;
    const coreColor = mixWhite(accent, 0.35);
    const hotColor = mixWhite(accent, 0.82);
    const shardColor = mixWhite(accent, 0.5);
    const backdropColor = shade(accent, 0.14);

    const { coverMs: COVER, holdMs: HOLD, revealMs: REVEAL } = TELEPORT;

    // ── 1) Kurtyna — ciemne pole, które zasłania podmianę sceny ──────────────
    const backdrop = this.add
      .rectangle(cx, cy, maxSide * 2, maxSide * 2, backdropColor, 1)
      .setScrollFactor(0)
      .setDepth(0)
      .setAlpha(0);
    this.tweens.add({ targets: backdrop, alpha: 1, duration: COVER * 0.75, ease: 'Sine.easeIn' });

    // ── 2) Portal — rdzeń + gorący środek + krążące odłamki (cała grupa wiruje) ─
    const portal = this.add.container(cx, cy).setScrollFactor(0).setDepth(10);
    const core = this.add.circle(0, 0, maxSide * 0.34, coreColor, 0.92).setBlendMode(Phaser.BlendModes.ADD);
    const hot = this.add.circle(0, 0, maxSide * 0.16, hotColor, 0.95).setBlendMode(Phaser.BlendModes.ADD);
    portal.add(core);
    portal.add(hot);

    const ringR = maxSide * 0.26;
    for (let i = 0; i < TELEPORT.ringShards; i++) {
      const a = (i / TELEPORT.ringShards) * Math.PI * 2;
      const shard = this.add
        .circle(Math.cos(a) * ringR, Math.sin(a) * ringR, 9, shardColor, 0.9)
        .setBlendMode(Phaser.BlendModes.ADD);
      portal.add(shard);
    }

    portal.setScale(0.05);
    this.tweens.add({ targets: portal, scale: 1, duration: COVER, ease: 'Back.easeIn' });
    this.tweens.add({ targets: portal, angle: TELEPORT.spinDegrees, duration: COVER, ease: 'Cubic.easeIn' });
    this.tweens.add({
      targets: hot,
      scale: { from: 0.78, to: 1.22 },
      duration: 320,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ── 3) Iskry zlatujące się do środka (energia „wsysana" do portalu) ──────
    for (let i = 0; i < TELEPORT.gatherSparks; i++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = maxSide * (0.5 + Math.random() * 0.28);
      const spark = this.add
        .circle(cx + Math.cos(ang) * dist, cy + Math.sin(ang) * dist, Phaser.Math.Between(3, 6), shardColor, 0.9)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setScrollFactor(0)
        .setDepth(12);
      this.tweens.add({
        targets: spark,
        x: cx,
        y: cy,
        scale: 0.2,
        duration: COVER * Phaser.Math.FloatBetween(0.6, 0.95),
        delay: Math.random() * COVER * 0.25,
        ease: 'Cubic.easeIn',
        onComplete: () => spark.destroy(),
      });
    }

    // ── 4) Biały rozbłysk — maskuje DOKŁADNY moment podmiany sceny ───────────
    const flash = this.add
      .rectangle(cx, cy, maxSide * 2, maxSide * 2, 0xffffff, 1)
      .setScrollFactor(0)
      .setDepth(100)
      .setName('teleport-flash')
      .setAlpha(0);

    // The flash masks the scene swap, not the download. A slow connection must
    // show a live portal with feedback instead of an apparently frozen white screen.
    const destination = this.target === 'PlazaScene' ? 'Plac handlowy'
      : this.target === 'AtlantisSanctumScene' ? 'Sanktuarium'
      : BIOMES_BY_ID[resolvePlayableBiomeId(saveManager.getCurrent())]?.name.pl ?? 'Nowy świat';
    const labelY = Math.min(h - 84, cy + Math.min(w, h) * 0.25);
    const loading = this.add.container(cx, labelY).setDepth(110).setName('teleport-loading').setVisible(false);
    const panelWidth = Math.min(420, w - 24);
    const panel = this.add.graphics().fillStyle(backdropColor, 0.94).lineStyle(1, coreColor, 0.35);
    panel.fillRoundedRect(-panelWidth / 2, -29, panelWidth, 110, 18);
    panel.strokeRoundedRect(-panelWidth / 2, -29, panelWidth, 110, 18);
    const heading = this.add.text(0, 0, `Podróż: ${destination}`, {
      fontFamily: '"Baloo 2", sans-serif', fontSize: `${w < 600 ? 19 : 25}px`,
      color: '#fff8e6', align: 'center', wordWrap: { width: Math.max(160, panelWidth - 24) },
    }).setOrigin(0.5);
    const progressWidth = Math.min(240, w - 64);
    const track = this.add.rectangle(0, 33, progressWidth, 6, 0xffffff, 0.2).setOrigin(0.5);
    const progress = this.add.rectangle(-progressWidth / 2, 33, 0, 6, coreColor).setOrigin(0, 0.5);
    const detail = this.add.text(0, 53, 'Szykujemy miejsce…', {
      fontFamily: '"Baloo 2", sans-serif', fontSize: '14px', color: '#e6edf3',
    }).setOrigin(0.5);
    loading.add([panel, heading, track, progress, detail]);
    const onProgress = (value: number): void => {
      progress.setSize(progressWidth * value, 6);
      detail.setText(`Szykujemy miejsce… ${Math.round(value * 100)}%`);
    };
    this.load.on(Phaser.Loader.Events.PROGRESS, onProgress);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.load.off(Phaser.Loader.Events.PROGRESS, onProgress));

    // ── 5) REVEAL — zaczyna się dopiero, gdy paczka celu jest naprawdę gotowa.
    // Błąd pojedynczego pliku jest fail-soft i nie blokuje zmiany.
    const reveal = (): void => {
      this.tweens.add({ targets: flash, alpha: 0, duration: REVEAL * 0.5, ease: 'Quad.easeOut' });
      this.tweens.add({ targets: backdrop, alpha: 0, duration: REVEAL, ease: 'Sine.easeOut' });
      this.tweens.add({
        targets: portal,
        scale: 1.6,
        alpha: 0,
        angle: TELEPORT.spinDegrees + 140,
        duration: REVEAL,
        ease: 'Cubic.easeOut',
      });
      for (let i = 0; i < TELEPORT.burstSparks; i++) {
        const ang = Math.random() * Math.PI * 2;
        const dist = maxSide * (0.32 + Math.random() * 0.4);
        const spark = this.add
          .circle(cx, cy, Phaser.Math.Between(3, 7), shardColor, 0.95)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setScrollFactor(0)
          .setDepth(12);
        this.tweens.add({
          targets: spark,
          x: cx + Math.cos(ang) * dist,
          y: cy + Math.sin(ang) * dist,
          alpha: 0,
          scale: 0.2,
          duration: REVEAL * Phaser.Math.FloatBetween(0.55, 0.9),
          ease: 'Cubic.easeOut',
          onComplete: () => spark.destroy(),
        });
      }
      this.time.delayedCall(REVEAL + 80, () => this.scene.stop());
    };

    let covered = false;
    let assetsReady = false;
    let finishing = false;
    const isCurrentRun = (): boolean => runId === this.runId && this.scene.isActive();
    const swapWhenReady = (): void => {
      if (!covered || !assetsReady || finishing || !isCurrentRun()) return;
      finishing = true;
      loading.setVisible(false);
      this.tweens.add({ targets: flash, alpha: 1, duration: 140, ease: 'Quad.easeIn', onComplete: () => {
        if (!isCurrentRun()) return;
        try {
          this.performSwap();
        } catch (err) {
          console.warn('[TeleportScene] Podmiana sceny nieudana:', err);
        } finally {
          if (isCurrentRun()) this.time.delayedCall(HOLD, reveal);
        }
      } });
    };
    this.time.delayedCall(COVER, () => {
      if (!isCurrentRun()) return;
      covered = true;
      loading.setVisible(!assetsReady);
      swapWhenReady();
    });

    // Start the download during COVER. Guard all continuations by run identity:
    // a stopped overlay may be relaunched before its previous load settles.
    // During create() Phaser is still CREATING; the asset manager deliberately
    // rejects inactive scenes. CREATE fires after the status becomes RUNNING.
    this.events.once(Phaser.Scenes.Events.CREATE, () => {
      void this.prepareTargetAssets(runId)
        .catch((err) => console.warn('[TeleportScene] Nieoczekiwany błąd teleportu:', err))
        .finally(() => {
          if (!isCurrentRun()) return;
          assetsReady = true;
          swapWhenReady();
        });
    });
  }

  private async prepareTargetAssets(runId: number): Promise<void> {
    try {
      // UI jest pod portalem, ale save może zmienić zewnętrzna synchronizacja.
      // Dla WorldScene raz ponawiamy load, jeśli cel zmienił się w trakcie.
      for (let attempt = 0; attempt < 2; attempt++) {
        if (runId !== this.runId || !this.scene.isActive()) return;
        const save = saveManager.getCurrent();
        const preparedBiome = this.target === 'WorldScene'
          ? resolvePlayableBiomeId(save)
          : undefined;
        await assetBundleManager.prepareTarget(this, this.target, save);
        if (
          this.target !== 'WorldScene'
          || preparedBiome === resolvePlayableBiomeId(saveManager.getCurrent())
        ) break;
      }
    } catch (err) {
      // Nie soft-lockujemy portalu z powodu infrastruktury loadera. Assety mają
      // własne fallbacki, a błąd zostaje widoczny w konsoli diagnostycznej.
      console.warn('[TeleportScene] Przygotowanie paczki celu nieudane:', err);
    }
  }

  /**
   * Podmiana scen świata z poziomu WŁASNEGO pluginu (TeleportScene zostaje
   * aktywna na wierzchu). Zatrzymuje aktualnie aktywną scenę świata i launchuje
   * cel na świeżo (restart, gdy cel = aktualna scena, np. zmiana biomu).
   *
   * Świadomie używamy `stop` + `launch` zamiast `start` (które zatrzymałoby
   * TeleportScene jako wołającego) ani domknięcia z pluginu umierającej sceny —
   * dzięki temu lista scen zostaje SPÓJNA i kolejne teleporty wstają poprawnie.
   * Idempotentne (`swapped`).
   */
  private performSwap(): void {
    if (this.swapped) return;
    this.swapped = true;

    // Każdy `stop()` w OSOBNYM try/catch: handler SHUTDOWN dowolnej sceny może
    // rzucić (np. dotknięcie już-zniszczonego `cameras.main`), a wyjątek NIE może
    // przerwać launchu celu — inaczej świat nie wstaje → zawieszony portal /
    // soft-lock powrotu (realny bug z proda). Launch celu jest święty.
    const stop = (key: TeleportTarget): void => {
      if (this.scene.isActive(key)) {
        try {
          this.scene.stop(key);
        } catch (err) {
          console.warn(`[TeleportScene] stop(${key}) rzucił (SHUTDOWN handler?):`, err);
        }
      }
    };

    for (const key of ['WorldScene', 'PlazaScene', 'AtlantisSanctumScene', 'HalloweenBossScene'] as const) {
      if (key !== this.target) stop(key);
    }
    // Cel: zatrzymaj jeśli aktywny (wymuś świeży start, np. restart biomu), potem launch.
    stop(this.target);
    try {
      this.scene.launch(this.target);
    } catch (err) {
      console.warn('[TeleportScene] launch celu nieudany:', err);
    }

    // Zostań na wierzchu nad nową sceną na czas fazy reveal.
    this.scene.bringToTop();
  }
}

// ─────────────────────────── Pomocnicze: kolory ──────────────────────────────

const clampChannel = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

/** Przyciemnia kolor mnożąc kanały RGB przez `factor` (0..1). */
function shade(color: number, factor: number): number {
  const c = Phaser.Display.Color.IntegerToColor(color);
  return Phaser.Display.Color.GetColor(
    clampChannel(c.red * factor),
    clampChannel(c.green * factor),
    clampChannel(c.blue * factor),
  );
}

/** Miesza kolor z bielą (`t` ∈ 0..1, 1 = czysta biel) — rozjaśnia rdzeń portalu. */
function mixWhite(color: number, t: number): number {
  const c = Phaser.Display.Color.IntegerToColor(color);
  return Phaser.Display.Color.GetColor(
    clampChannel(c.red + (255 - c.red) * t),
    clampChannel(c.green + (255 - c.green) * t),
    clampChannel(c.blue + (255 - c.blue) * t),
  );
}
